import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { RoleLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { userRoleURL } from '../../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import FormRow from '../../Common/Components/FormRow'
import { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import Modal from '../../Common/Components/Modal'
import { GroupedSelect, StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { SelectItem } from '../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { NONE_VALUE_STRING_DEFAULT } from '../../Common/Constants/DefaultValues'
import { DocTopic } from '../../Common/Constants/Documentation'
import { ReactState, UserInfoContext } from '../../Common/Contexts'
import { ServletPathContextProvider } from '../../Common/Contexts/ServletPathContext'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { StateSetter } from '../../Common/Types/Types'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { displayError } from '../../Common/Utils/Messages'
import { DEFAULT_PASSWORD_VALUE } from '../AddUserMapper'
import { DEFAULT_CME_ID, SERVER_NONE_VALUE } from '../Constants'
import { CustomiseRole } from '../CustomiseRole/CustomiseRole'
import { mapValuesToPermissions, useMapPermissionsToFormValues } from '../CustomiseRole/RoleMapper'
import {
    AGENTS_PERM_NAME,
    CME_PERM_NAME,
    DEVICES_PERM_NAME,
    PASSWORD_COMPONENT_NAME,
    REPORTING_LEVEL_NAME,
    ROLE_COMPONENT_NAME,
    ROLE_PERMS_NAMESPACE
} from '../FieldNames'
import { CustomRolesForm, ManageCustomRole } from '../ManageCustomRole/Main/ManageCustomRole'
import { FormRolePermissions, UserRoleMetadata } from '../Types'
import { RoleTemplate } from './Templates'
import Custom from './Templates/Custom.json'
import None from './Templates/None.json'
import { CustomRoleDTO } from './Types/Types'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { $window } from '../../../NonReact/common/AkixiWindow'

type Props = {
    adminPermissions: string[]
    setCurrentPermissions: StateSetter<string[]>
    viewOnly?: boolean
    customUserRoles: RoleTemplate[]
    customRoleMetadata: Nullable<UserRoleMetadata>
    getCustomRoles: () => Promise<void>
}

/**
 * Role tab content.
 *
 * @param adminPermissions - Permissions from the user API.
 */
export const Role: FC<Props> = ({
    adminPermissions,
    setCurrentPermissions,
    viewOnly,
    customUserRoles,
    customRoleMetadata,
    getCustomRoles
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('RoleLanguage')

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [customiseOpen, setCustomiseOpen]: ReactState<boolean> = useState<boolean>(false)
    const [addRoleOpen, setAddRoleOpen]: ReactState<boolean> = useState<boolean>(false)
    const [customiseAllowed, setCustomiseAllowed]: ReactState<boolean> = useState<boolean>(false)

    const { watch, setValue, trigger } = useFormContext()

    const allRoles: RoleTemplate[] = useMemo(
        (): RoleTemplate[] => [
            None,
            ...customUserRoles.filter((role: RoleTemplate) => !role.isCustomRole),
            Custom,
            ...customUserRoles.filter((role: RoleTemplate) => role.isCustomRole)
        ],
        [customUserRoles]
    )
    const passwordValue: string = watch(PASSWORD_COMPONENT_NAME)
    const cmePermissionValue: string = watch(CME_PERM_NAME)
    const roleValue: string = watch(ROLE_COMPONENT_NAME)
    const permissionsFromForm: Definable<FormRolePermissions> = watch(ROLE_PERMS_NAMESPACE)

    const currentRole: Nullable<RoleTemplate> = useMemo(
        (): Nullable<RoleTemplate> =>
            allRoles.find((roleTemplate: RoleTemplate): boolean => roleTemplate.value === roleValue),
        [roleValue]
    )

    const currentPermissionsFromForm: string[] = useMemo(
        (): string[] => mapValuesToPermissions(permissionsFromForm),
        [permissionsFromForm]
    )

    const resetPassword = (newPermissions: string[]): void => {
        if (passwordValue === DEFAULT_PASSWORD_VALUE && adminPermissions.length === 0 && newPermissions.length > 0) {
            setValue(PASSWORD_COMPONENT_NAME, '')
        }
    }

    const GROUP_TEMPLATE: StandardOptionGroup[] = [
        {
            options: []
        },
        {
            groupTitle: language.get(RoleLanguage.RoleReportingGroup),
            options: []
        },
        {
            groupTitle: language.get(RoleLanguage.RoleAdminGroup),
            options: []
        },
        {
            groupTitle: language.get(RoleLanguage.UserCustomizedRoles),
            options: []
        }
    ]

    const roleOptions: StandardOptionGroup[] = allRoles
        .filter(({ value, permissions }: RoleTemplate): boolean => {
            // The custom value is always available.
            if (value === Custom.value) {
                return true
            }
            // Check to see if the current user is allowed to set the user to each role.
            return !!permissions?.every(
                (permission: string): boolean => !!userInfo?.Permissions?.Admin?.includes(permission)
            )
        })
        .reduce(
            // Map the role template to select options.
            (
                [noneOptions, reportingOptions, adminOptions, userCustomizedOptions]: StandardOptionGroup[],
                { name, value, requiresReporting, isCustomRole }: RoleTemplate
            ): StandardOptionGroup[] => {
                const newOption: SelectItem = {
                    label: language.get((RoleLanguage as unknown as Record<string, string>)[name]) || name,
                    value
                }

                if (name === None.name && noneOptions.options.length === 0) {
                    noneOptions.options.push(newOption)
                } else if (requiresReporting === true) {
                    reportingOptions.options.push(newOption)
                } else if (isCustomRole) {
                    userCustomizedOptions.options.push(newOption)
                } else {
                    adminOptions.options.push(newOption)
                }

                return [noneOptions, reportingOptions, adminOptions, userCustomizedOptions]
            },
            GROUP_TEMPLATE
        )
        .filter((group: StandardOptionGroup): boolean => group.options.length > 0)

    const mapPermissionsFromForm = (value: string): void =>
        setValue(
            ROLE_PERMS_NAMESPACE,
            useMapPermissionsToFormValues(
                allRoles.find((roleTemplate: RoleTemplate): boolean => roleTemplate.value === value)?.permissions
            )
        )

    const mapPermissions = (currentPermissions: string[], dontMapToForm?: boolean): void => {
        const sortedPermissions: string[] = [...currentPermissions].sort()
        const matchedRoles: Definable<RoleTemplate>[] = allRoles.filter(
            ({ permissions }: RoleTemplate): boolean =>
                permissions?.length === currentPermissions.length &&
                sortedPermissions.every((perm: string): boolean => permissions.includes(perm))
        )

        setTimeout((): void => {
            if (isMounted.current) {
                if (matchedRoles) {
                    const reportingLevel: string = watch(REPORTING_LEVEL_NAME)
                    const matched: RoleTemplate =
                        matchedRoles.find(
                            (role: Definable<RoleTemplate>): boolean =>
                                !!(role && IsVarEmpty(role.requiresReporting)) ||
                                !!(
                                    role &&
                                    role.requiresReporting ===
                                        (!IsStringEmpty(reportingLevel) && reportingLevel !== SERVER_NONE_VALUE)
                                )
                        ) ?? new RoleTemplate()

                    setValue(ROLE_COMPONENT_NAME, matched.value)
                } else {
                    setValue(ROLE_COMPONENT_NAME, Custom.value)
                }
            }
        })

        setCurrentPermissions(sortedPermissions)

        if (!dontMapToForm) {
            setValue(ROLE_PERMS_NAMESPACE, useMapPermissionsToFormValues(currentPermissions))
        }
    }

    const closeCustomise = (): void => {
        setCustomiseOpen(false)
    }

    const closeAddRole = (): void => {
        setAddRoleOpen(false)
    }

    const handleCustomiseSubmit = (rolePermissions: Record<string, Record<string, boolean>>): void => {
        const newPermissions: string[] = mapValuesToPermissions(rolePermissions)
        mapPermissions(newPermissions, true)
        setValue(ROLE_PERMS_NAMESPACE, rolePermissions)

        resetPassword(newPermissions)

        setTimeout((): void => {
            trigger(PASSWORD_COMPONENT_NAME)
            trigger(REPORTING_LEVEL_NAME)
            trigger(ROLE_COMPONENT_NAME)
        })
    }

    const onCloseAddRole = (name?: string): void => {
        closeAddRole()
        getCustomRoles().then((): void => {
            if (name) {
                setTimeout((): void => {
                    setValue(ROLE_COMPONENT_NAME, name.replace(/\s/g, '_').toUpperCase())
                }, 100)
            }
        })
        clearModalHelpTopic()
    }

    useEffect(() => {
        if (userInfo && adminPermissions) {
            mapPermissions(adminPermissions, true)
            setCustomiseAllowed(
                !!adminPermissions.every(
                    (permission: string): boolean => userInfo?.Permissions?.Admin.includes(permission)
                )
            )
        }
    }, [JSON.stringify(adminPermissions), userInfo, JSON.stringify(allRoles)])

    return (
        <>
            {customiseAllowed ? (
                <div className='role-tab tab-area'>
                    <FormRow className='user-role'>
                        <ValidationErrors name={ROLE_COMPONENT_NAME}>
                            <div className='custom-select-role'>
                                <GroupedSelect
                                    variant='outlined'
                                    name={ROLE_COMPONENT_NAME}
                                    defaultValue={NONE_VALUE_STRING_DEFAULT}
                                    label={language.get(RoleLanguage.UserRoleLabel)}
                                    options={roleOptions}
                                    handleChange={(value: string): void => {
                                        const newPermissions: string[] = [
                                            ...(allRoles.find((role: RoleTemplate): boolean => role.value === value)
                                                ?.permissions ?? [])
                                        ].sort()

                                        setCurrentPermissions(newPermissions)

                                        if (value === Custom.value) {
                                            return
                                        }

                                        mapPermissionsFromForm(value)

                                        trigger(REPORTING_LEVEL_NAME)

                                        resetPassword(newPermissions)

                                        setTimeout((): void => {
                                            trigger(PASSWORD_COMPONENT_NAME)
                                            trigger(DEVICES_PERM_NAME)
                                            trigger(AGENTS_PERM_NAME)
                                        })
                                    }}
                                    rules={{
                                        validate: (value: string): ValidateResult => {
                                            const role: Definable<RoleTemplate> = allRoles.find(
                                                (roleTemp) => roleTemp.value === value
                                            )

                                            if (role && !IsVarEmpty(role.requiresReporting)) {
                                                const reportingLevel: string = watch(REPORTING_LEVEL_NAME)
                                                const isReportingLevel: boolean =
                                                    !IsStringEmpty(reportingLevel) &&
                                                    reportingLevel !== SERVER_NONE_VALUE
                                                const errorMessage: string = !isReportingLevel
                                                    ? language.get(RoleLanguage.RoleRequiresReportingLevel)
                                                    : language.get(RoleLanguage.RoleRequiresNoReportingLevel)

                                                return role.requiresReporting === isReportingLevel
                                                    ? undefined
                                                    : errorMessage
                                            }

                                            return undefined
                                        }
                                    }}
                                    disabled={viewOnly}
                                />
                                {!viewOnly && (
                                    <IconButton
                                        customClassName='csar-edit'
                                        iconName={IconName.CUSTOMISE_ROLE}
                                        type={IconButtonType.BUTTON}
                                        onClick={(): void => setCustomiseOpen(true)}
                                    />
                                )}
                                {/* Add new custom role */}
                                {!viewOnly &&
                                    (customRoleMetadata?.UsedCustomRoleSlots ?? 0) <=
                                        (customRoleMetadata?.TotalCustomRoleSlots ?? 0) &&
                                    PermissionsUtil.hasPermissions(
                                        userInfo,
                                        PermissionsUtil.builder(AdminBackendPermissionNames.USER_ROLE).create()
                                    ) &&
                                    roleValue === Custom.value &&
                                    currentPermissionsFromForm?.length > 0 && (
                                        <IconButton
                                            title={language.get(RoleLanguage.CreateCustomRole)}
                                            customClassName='csar-add'
                                            iconName={IconName.ROLE_ADD}
                                            type={IconButtonType.BUTTON}
                                            onClick={(): void => {
                                                setModalHelpTopicID(DocTopic.ADMIN_MANAGE_USER_ROLES)
                                                setAddRoleOpen(true)
                                            }}
                                        />
                                    )}

                                {!viewOnly &&
                                    currentRole?.isCustomRole &&
                                    PermissionsUtil.hasPermissions(
                                        userInfo,
                                        PermissionsUtil.builder(AdminBackendPermissionNames.USER_ROLE).delete()
                                    ) && (
                                        <IconButton
                                            title={language.get(RoleLanguage.DeleteCustomRole)}
                                            customClassName='csar-delete'
                                            iconName={IconName.DELETE}
                                            type={IconButtonType.BUTTON}
                                            onClick={(): void => {
                                                requestJSON(userRoleURL(currentRole.value), {
                                                    method: RESTMethods.DELETE
                                                }).then(() => {
                                                    getCustomRoles().then(() =>
                                                        setTimeout(
                                                            (): void => setValue(ROLE_COMPONENT_NAME, Custom.value),
                                                            100
                                                        )
                                                    )
                                                })
                                            }}
                                        />
                                    )}
                            </div>
                        </ValidationErrors>

                        <div
                            className='role-description'
                            dangerouslySetInnerHTML={{
                                __html: isVarNotEmpty(currentRole?.description)
                                    ? LanguageUtils.sanitiseHTML(currentRole?.description ?? EMPTY_STRING)
                                    : language.get(
                                          (RoleLanguage as unknown as Record<string, string>)[
                                              `Role:${currentRole?.value}`
                                          ]
                                      )
                            }}
                        ></div>

                        <Modal isOpen={customiseOpen} onClose={closeCustomise} isClosableWithXButton={true} layered>
                            <CustomiseRole
                                cmePermissionValue={cmePermissionValue}
                                rolePermissions={watch(ROLE_PERMS_NAMESPACE)}
                                closeModal={closeCustomise}
                                onSubmit={handleCustomiseSubmit}
                            />
                        </Modal>

                        <Modal
                            isOpen={addRoleOpen}
                            onClose={onCloseAddRole}
                            isClosableWithXButton={true}
                            layered
                            classNames='manage-custom-role-modal'
                        >
                            <ServletPathContextProvider value={$window.sSERVLETPATH}>
                                <ManageCustomRole
                                    handleOnSubmit={(data: CustomRolesForm): void => {
                                        const permissions: string[] = mapValuesToPermissions(
                                            watch(ROLE_PERMS_NAMESPACE)
                                        )
                                        const roleDTO: CustomRoleDTO = {
                                            Name: data.roleName,
                                            CMEnvironmentID: userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID,
                                            Permissions: permissions,
                                            Description: data.description
                                        }

                                        requestJSON(userRoleURL(), {
                                            method: RESTMethods.POST,
                                            body: roleDTO
                                        })
                                            .then((): void => onCloseAddRole(data.roleName))
                                            .catch(displayError)
                                    }}
                                    closeModal={closeAddRole}
                                />
                            </ServletPathContextProvider>
                        </Modal>
                    </FormRow>
                </div>
            ) : (
                language.get(RoleLanguage.RolePermissionsTooLow)
            )}
        </>
    )
}
